import type { PopperProps } from '@mui/material';
import type { NavListProps } from 'src/legacy/components/nav-section';
import type { AppRoute } from 'src/routes/types';

import {
  Autocomplete,
  Box,
  ClickAwayListener,
  InputAdornment,
  InputBase,
  Popper,
  Slide,
} from '@mui/material';
// @mui
import { alpha, styled } from '@mui/material/styles';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { memo, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButtonAnimate } from 'src/legacy/components/animate';
// components
import Iconify from 'src/legacy/components/iconify';
import SearchNotFound from 'src/legacy/components/search-not-found';
//
import { useLocales } from 'src/legacy/locales';
// utils
import { bgBlur } from 'src/legacy/utils/cssStyles';
import flattenArray from 'src/legacy/utils/flattenArray';

import NavConfig from '../nav/config-navigation';

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  alignItems: 'center',
  boxShadow: theme.customShadows.z8,
  display: 'flex',
  height: APPBAR_MOBILE,
  left: 0,
  padding: theme.spacing(0, 3),
  position: 'absolute',
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
  top: 0,
  width: '100%',
  zIndex: 99,
}));

const StyledPopper = styled((props: PopperProps) => <Popper {...props} />)(
  ({ theme }) => ({
    '& .MuiAutocomplete-listbox': {
      '& .MuiAutocomplete-option': {
        '&:hover': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.hoverOpacity
          ),
          borderColor: theme.palette.primary.main,
        },
        '&:last-of-type': {
          borderBottomColor: 'transparent',
        },
        border: `dashed 1px transparent`,
        borderBottomColor: theme.palette.divider,
        display: 'block',
        margin: 0,
        padding: theme.spacing(0.5, 2),
      },
    },
    '& .MuiAutocomplete-paper': {
      padding: theme.spacing(1, 0),
    },
    '& .MuiListSubheader-root': {
      '&.MuiAutocomplete-groupLabel': {
        ...bgBlur({ color: theme.palette.background.neutral }),
        ...theme.typography.overline,
        borderRadius: theme.shape.borderRadius,
        lineHeight: '48px',
        margin: 0,
        top: 0,
      },
    },
    left: `8px !important`,
    [theme.breakpoints.up('md')]: {
      top: `${APPBAR_DESKTOP + 8}px !important`,
    },
    top: `${APPBAR_MOBILE + 8}px !important`,
    transform: 'none !important',
    width: 'calc(100% - 16px) !important',
  })
);

// ----------------------------------------------------------------------

interface Option extends NavListProps {
  subheader: string;
}

type SearchbarProps = {
  privateAppRoutes: AppRoute[];
};

const Searchbar = ({ privateAppRoutes }: SearchbarProps) => {
  const navigate = useNavigate();

  const { translate } = useLocales();

  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const reduceItems = NavConfig(privateAppRoutes)
    .map((list) => handleLoop(list.items, (list as any).subheader))
    .flat();

  const allItems = flattenArray(reduceItems).map((option) => {
    const group = splitPath(reduceItems, option.path);

    return {
      group:
        group && group.length > 1 ? group[0] : (option as Option).subheader,
      indexKey: 'minimal',
      path: option.path,
      title: option.title,
    };
  });

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (path: string) => {
    if (path.includes('http')) {
      window.open(path);
    } else {
      navigate(path);
    }
    handleClose();
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleClick(searchQuery);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButtonAnimate onClick={handleOpen}>
            <Iconify icon="eva:search-fill" />
          </IconButtonAnimate>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <StyledSearchbar>
            <Autocomplete
              autoHighlight
              disableClearable
              disablePortal
              getOptionLabel={(option) =>
                `${option.title} ${option.path} ${option.indexKey}`
              }
              groupBy={(option) => option.group}
              noOptionsText={
                <SearchNotFound query={searchQuery} sx={{ py: 10 }} />
              }
              onInputChange={(event, value) => setSearchQuery(value)}
              options={allItems.sort((a, b) => -b.group.localeCompare(a.group))}
              PopperComponent={StyledPopper}
              popupIcon={null}
              renderInput={(params) => (
                <InputBase
                  {...params.InputProps}
                  autoFocus
                  fullWidth
                  inputProps={params.inputProps}
                  onKeyUp={handleKeyUp}
                  placeholder={`${translate('search')}...`}
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify
                        icon="eva:search-fill"
                        sx={{ color: 'text.disabled' }}
                      />
                    </InputAdornment>
                  }
                  sx={{ height: 1, typography: 'h6' }}
                />
              )}
              renderOption={(props, option, { inputValue }) => {
                const { path, title } = option;

                const partsTitle = parse(title, match(title, inputValue));

                const partsPath = parse(path, match(path, inputValue));

                return (
                  <Box
                    component="li"
                    {...props}
                    onClick={() => handleClick(path)}
                  >
                    <div>
                      {partsTitle.map((part, index) => (
                        <Box
                          component="span"
                          key={index}
                          sx={{
                            color: part.highlight
                              ? 'primary.main'
                              : 'text.primary',
                            textTransform: 'capitalize',
                            typography: 'subtitle2',
                          }}
                        >
                          {part.text}
                        </Box>
                      ))}
                    </div>

                    <div>
                      {partsPath.map((part, index) => (
                        <Box
                          component="span"
                          key={index}
                          sx={{
                            color: part.highlight
                              ? 'primary.main'
                              : 'text.secondary',
                            typography: 'caption',
                          }}
                        >
                          {part.text}
                        </Box>
                      ))}
                    </div>
                  </Box>
                );
              }}
              sx={{ height: 1, width: 1 }}
            />
          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
};

export default memo(Searchbar);

// ----------------------------------------------------------------------

type ItemProps = {
  currItem: NavListProps;
  path: string[];
};

const splitPath = (array: NavListProps[], key: string) => {
  let stack = array.map((item) => ({
    currItem: item,
    path: [item.title],
  }));

  while (stack.length) {
    const { currItem, path } = stack.pop() as ItemProps;

    if (currItem.path === key) {
      return path;
    }

    if (currItem.children?.length) {
      stack = stack.concat(
        currItem.children.map((item: NavListProps) => ({
          currItem: item,
          path: path.concat(item.title),
        }))
      );
    }
  }
  return null;
};

const handleLoop = (array: any, subheader?: string) => {
  return array?.map((list: any) => ({
    subheader,
    ...list,
    ...(list.children && {
      children: handleLoop(list.children, subheader),
    }),
  }));
};
